/**
 * Missed Exits API client.
 *
 * Two endpoints fronted by the same admin Lambda:
 *
 *   GET  /admin/account-manager/terminal/missed-exits
 *        → List positions across the in-scope pool that fall into one of:
 *            - OPEN whose scheduled exit moment is in the past
 *            - EXIT_PENDING that have been stuck for more than 5 minutes
 *          SuperAdmin without ?account_manager_user_id sees every active
 *          trading user. AccountManagers see their own pool only.
 *
 *   POST /admin/account-manager/terminal/missed-exits/exit
 *        Body: { user_id, position_id }
 *        → Force-exit a single position via exit_service.exit_positions_batch.
 *
 * The list response is a snapshot — the page should refresh after a successful
 * force-exit to catch new state.
 */
import apiClient from './apiClient';
import type { ApiResponse } from '../types';

const API_TYPE = 'api' as const;

export type MissedExitReason = 'open_past_exit_time' | 'exit_pending_stuck';

export interface MissedExitRow {
  user_id: string;
  user_email: string;
  user_name: string;
  position_id: string;
  strategy_id: string;
  strategy_owner_id: string;
  strategy_name: string;
  basket_id: string;
  basket_name: string;
  broker: string;
  client_id: string;
  symbol: string;
  exchange: string;
  quantity: number;
  action: string;
  position_status: string;
  execution_date: string;
  entry_filled_at: string;
  average_entry_price: string;
  exit_placed_at: string;
  exit_chase_started_at: string;
  scheduled_exit_time: string;
  reason: MissedExitReason;
  /** Minutes the position has been stuck in EXIT_PENDING; null for OPEN-past-exit rows. */
  stuck_minutes: number | null;
  /** Broker login state for this account right now. A logged-out session is the
   *  usual cause of the miss — and means a Force exit will be skipped until the
   *  user logs in. null = the login check itself failed (render as 'Unknown'). */
  logged_in: boolean | null;
}

export interface MissedExitsListResponse {
  rows: MissedExitRow[];
  truncated: boolean;
  as_of: string;
  scope_user_count: number;
  stuck_threshold_minutes: number;
}

export interface ForceExitResponse {
  exits_placed: number;
  /** OPEN leg whose exit was deferred (SL claim mid-flight); still OPEN, retry. */
  exits_deferred?: number;
  position_id: string;
  user_id: string;
  prior_status: string;
}

function unwrap<T>(response: ApiResponse<T> | T, errorMsg: string): T {
  if ((response as any)?.success !== undefined) {
    const wrapped = response as ApiResponse<T>;
    if (!wrapped.success || !wrapped.data) {
      throw new Error(wrapped.message || errorMsg);
    }
    return wrapped.data;
  }
  return response as T;
}

class MissedExitsService {
  /**
   * List missed exits for the in-scope pool. SuperAdmins may scope to another
   * AM's pool with the `accountManagerUserId` argument; non-SuperAdmin callers
   * receive a 403 if they pass it.
   */
  async list(accountManagerUserId?: string): Promise<MissedExitsListResponse> {
    const query = accountManagerUserId
      ? `?account_manager_user_id=${encodeURIComponent(accountManagerUserId)}`
      : '';
    try {
      const response = await apiClient.get<
        ApiResponse<MissedExitsListResponse> | MissedExitsListResponse
      >(
        `/admin/account-manager/terminal/missed-exits${query}`,
        undefined,
        API_TYPE,
      );
      return unwrap(response, 'Failed to load missed exits');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message
          || error.response?.data?.error
          || error.message
          || 'Failed to load missed exits',
      );
    }
  }

  /**
   * Force-exit a single missed position. Returns the backend's count of fresh
   * broker orders placed (`exits_placed`) — zero is a valid response when the
   * position was already in EXIT_PENDING and the broker_orders_sync chase
   * owns the recovery.
   */
  async forceExit(userId: string, positionId: string): Promise<ForceExitResponse> {
    try {
      const response = await apiClient.post<
        ApiResponse<ForceExitResponse> | ForceExitResponse
      >(
        '/admin/account-manager/terminal/missed-exits/exit',
        { user_id: userId, position_id: positionId },
        undefined,
        API_TYPE,
      );
      return unwrap(response, 'Failed to force-exit position');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message
          || error.response?.data?.error
          || error.message
          || 'Failed to force-exit position',
      );
    }
  }
}

const missedExitsService = new MissedExitsService();
export default missedExitsService;
export { MissedExitsService };
